import React, { useEffect, useState } from "react";
// NAVIGATION
import { useNavigate, useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Empty, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetBanksQuery, useLazyGetBanksByServerQuery } from "../../../redux/banks/bankAPI";
import { updateOpenDrawerBank, updateBank } from "../../../redux/banks/bankSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import validatePermission from "../../../utils/validatePermissions";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
import FilterSelectLiftType from "../../components/filters/FilterSelectLiftType";
// COMPONENTS
import BanksTableAction from "./BanksTableAction";
import TableColumnStatus from "../../components/table/TableColumnStatus";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";

const BanksTable = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const navigate = useNavigate();
	const { Column } = Table;
	const location = useLocation();
	const idServer = new URLSearchParams(location.search).get("idServer");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [banks, setBanks] = useState<BankInterface[]>([]);
	const [filterName, setFilterName] = useState("");
	const [filterLiftType, setFilterLiftType] = useState("");
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
	const { bankRefreshTable } = useSelector((state: RootState) => state.bank);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetBanks,
		{ data: dataGetBanks, isLoading: isLoadingGetBanks, isError: isErrorGetBanks, isFetching: isFetchingGetBanks },
	] = useLazyGetBanksQuery();
	const [
		triggerGetBanksByServer,
		{
			data: dataGetBanksByServer,
			isLoading: isLoadingGetBanksByServer,
			isError: isErrorGetBanksByServer,
			isFetching: isFetchingGetBanksByServer,
		},
	] = useLazyGetBanksByServerQuery();

	// ************************************************ */
	// TABLE ACTIONS ************************************ */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onCellNameClick = (record: any) => {
		navigate({
			pathname: "/edge-management/lifts",
			search: `?idBank=${record.id}&type=${record.type}${idServer && idServer !== "" ? `&idServer=${idServer}` : ""}`,
		});
	};

	const onCellClick = (record: BankInterface) => {
		dispatch(updateBank(record));
		dispatch(updateOpenDrawerBank(true));
	};

	const getData = async (page: number, limit: number) => {
		let searchAndFilterString = buildFilterString({
			filterName,
			filterLiftType,
		});
		if (userLogged && userLogged.org_id && userLogged.org_id !== globalOrganizationId) {
			searchAndFilterString = searchAndFilterString.concat(`&org_id=${globalOrganizationId}`);
		}
		const token = await GETJwtToken();
		const BODY: any = {
			page,
			limit,
			token,
			idServer,
			searchAndFilterString,
		};
		if (idServer && idServer !== "") {
			triggerGetBanksByServer(BODY);
		} else {
			triggerGetBanks(BODY);
		}
	};

	const handleTableChange = (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
		getData(pagination_.current, pagination_.pageSize);
	};

	const refreshTable = async () => {
		setPagination({
			...pagination,
			current: 1,
			pageSize: 10,
		});
		getData(1, 10);
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		const newData = structuredClone(dataGetBanks || dataGetBanksByServer);
		if (newData && newData.data) {
			setPagination({
				...pagination,
				total: newData.total,
			});
			setBanks(newData.data);
		}
	}, [dataGetBanks, dataGetBanksByServer]);

	useEffect(() => {
		if (globalOrganizationId && globalOrganizationId !== "") {
			getData(pagination.current, pagination.pageSize);
		}
	}, [globalOrganizationId, idServer]);

	useEffect(() => {
		if (bankRefreshTable) {
			getData(pagination.current, pagination.pageSize);
		}
	}, [bankRefreshTable]);

	useEffect(() => {
		if (isErrorGetBanks || isErrorGetBanksByServer) {
			setBanks([]);
		}
	}, [isErrorGetBanks, isErrorGetBanksByServer]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='mt-3'>
			{validatePermission("banks-view", permissions) ? (
				<Table
					rowClassName={(record, index) => getTableRowClass(index, theme)}
					className='my-custom-table'
					dataSource={banks}
					loading={isLoadingGetBanks || isFetchingGetBanks || isLoadingGetBanksByServer || isFetchingGetBanksByServer}
					rowKey={(record) => record.id}
					size='small'
					pagination={pagination}
					onChange={handleTableChange}
					scroll={{ x: 900 }}
					footer={() => (
						<div className='generalStyles__flexEnd'>
							<Tag>{pagination.total} Rows</Tag>
						</div>
					)}
				>
					{/** NAME */}
					<Column
						onCell={(record) => ({
							onClick: () => {
								onCellNameClick(record);
							},
						})}
						title={t("edgeManagement.banks.bankName")}
						dataIndex='name'
						key='name'
						className='generalStyles__tableHeaderLink'
						filterIcon={<FontAwesomeIcon icon={faSearch} />}
						filterDropdown={() => (
							<FilterSearchText
								placeHolder={t("general.name")}
								filterValue={filterName}
								setFilterValue={setFilterName}
								refreshTable={refreshTable}
								refreshRequireParamenters
							/>
						)}
						width='200px'
						render={(text) => (
							<>
								<div>
									<Tooltip title={t("general.navigateToLifts")}>{text}</Tooltip>
								</div>
							</>
						)}
					/>
					{/** TYPE -- */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						title={t("edgeManagement.lifts.type")}
						dataIndex='type'
						key='type'
						width='100px'
						filterIcon={<FontAwesomeIcon icon={faSearch} />}
						filterDropdown={() => (
							<FilterSelectLiftType
								placeHolder={t("general.liftType")}
								setFilterValue={setFilterLiftType}
								filterValue={filterLiftType}
								refreshTable={() => {
									refreshTable();
								}}
							/>
						)}
						render={(text) => (
							<>
								<div>
									{text === "ELEVATOR" && (
										<Tag color='blue' className='drawer__typeTag'>
											{text}
										</Tag>
									)}
									{text === "ESCALATOR" && (
										<Tag color='volcano' className='drawer__typeTag'>
											{text}
										</Tag>
									)}
									{text === "MOVINGWALK" && (
										<Tag color='green' className='drawer__typeTag'>
											{text}
										</Tag>
									)}
									{text === "BOT" && (
										<Tag color='magenta' className='drawer__typeTag'>
											{text}
										</Tag>
									)}
									{text === "GPIO" && (
										<Tag color='cyan' className='drawer__typeTag'>
											{text}
										</Tag>
									)}
								</div>
							</>
						)}
					/>
					{/** BANK TYPE -- */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						title={t("edgeManagement.banks.configurationType")}
						dataIndex='configurationType'
						key='configurationType'
						width='100px'
						filterIcon={<FontAwesomeIcon icon={faSearch} />}
						render={(text, record: any) => (
							<>
								<div>
									{record && record.adapter ? (
										<Tag color='purple' className='drawer__typeTag'>
											ADAPTER
										</Tag>
									) : (
										<Tag color='green' className='drawer__typeTag'>
											CONFIGURATION
										</Tag>
									)}
								</div>
							</>
						)}
					/>

					{/** SERVER NAME */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						width='150px'
						title={t("edgeManagement.servers.server")}
						render={(text, record: any) => (
							<>
								<div>
									{record && record.channel && record.channel.server && record.channel.server.name
										? record.channel.server.name
										: ""}
								</div>
							</>
						)}
					/>
					{/** CHANNEL NAME */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						width='150px'
						title={t("edgeManagement.channels.channel")}
						render={(text, record: any) => (
							<>
								<div>{record && record.channel && record.channel.name ? record.channel.name : "--"}</div>
							</>
						)}
					/>
					{/** ADAPTER NAME */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						width='150px'
						title={t("general.adapter")}
						render={(text, record: any) => (
							<>
								<div>{record && record.adapter && record.adapter.name ? record.adapter.name : "NA"}</div>
							</>
						)}
					/>
					{/** TOTAL LIFTS */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						width='75px'
						title={t("edgeManagement.lifts.lifts")}
						dataIndex='total'
						key='total'
						render={(text, record: any) => (
							<>
								<div>{record && record.lifts ? record.lifts.length : 0}</div>
							</>
						)}
					/>
					{/** LOGICAL PORT */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						title={t("general.logicalPort")}
						dataIndex='logical_port'
						key='logical_port'
						width='200px'
						className='generalStyles__hoverCursor'
						render={(text) => <>{text}</>}
					/>
					{/** STATUS */}
					<Column
						onCell={(record: BankInterface) => ({
							onClick: () => {
								onCellClick(record);
							},
						})}
						title={t("organizationManagement.status")}
						dataIndex='status'
						key='status'
						width='200px'
						className='generalStyles__hoverCursor'
						render={(text, record: BankInterface) => <TableColumnStatus active={record.active} />}
					/>
					{/** ACTIONS */}
					<Column
						title={t("edgeManagement.banks.actions")}
						key='action'
						width='150px'
						render={(_: any, record: BankInterface) => (
							<div>
								<BanksTableAction selectedItem={record} />
							</div>
						)}
					/>
				</Table>
			) : (
				<>
					<div className='generalStyles__noAccessToListTable'>
						<Empty description={t("edgeManagement.banks.listNotAvailable")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
					</div>
				</>
			)}
		</div>
	);
};

export default BanksTable;
